import { ModelBase } from 'store/model-base';
import { TradeInDeviceStatus, TradeInOfferStatus, TradeInProgram } from 'my-account/constants';
import { Manufacturer } from 'core/constants';

export class TradeInOffer extends ModelBase {
    public static storeName: string = 'tradeInOffer';

    public offerId: string;
    public device: TradeInDeviceDetails;
    public courier: string;
    public trackingNumber: string;
    public shippingLabelUrl: string;
    public offer: TradeInOfferDetails;
    public adjustedOffer: AdjustedOfferDetails;
    public adjustedOfferAmount: string;
    public decisionDeadline: string;
    public receivedDevice: ReceivedDeviceDetails;
    public isAdjustedOfferLessThanEstimated: boolean;
    public isITI: boolean;
    public isTIPorITI: boolean;

    public static create<T extends ModelBase>(data: ApiResponse): T { 
        data.adjustedOffer = data?.offer?.adjustedOffer || {};
        data.adjustedOfferAmount = data?.adjustedOffer?.amount;
        data.decisionDeadline = data?.adjustedOffer?.decisionDeadline;
        data.isAdjustedOfferLessThanEstimated = parseFloat(data?.adjustedOfferAmount) < parseFloat(data?.offer?.tipAmount);
        data.isITI = data?.offer?.program === TradeInProgram.ITI;
        data.isTIPorITI = data?.offer?.program === TradeInProgram.TIP || data?.offer?.program === TradeInProgram.ITI;
        
        return super.create<T>(data);
    }

    public get isOrderPlaced(): boolean {
        return (!this.offer.status || this.offer.status === TradeInOfferStatus.ACCEPTED_DEFERRED)
        && !(this.device.status === TradeInDeviceStatus.IN_TRANSIT
            || this.device.status === TradeInDeviceStatus.GOOD_CONDITION
            || this.device.status === TradeInDeviceStatus.FMIP_ON
            || this.device.status === TradeInDeviceStatus.REJECTED);
    }

    public get isEarlyUpgradeSendInDevice(): boolean {
        return (!this.offer.status || this.offer.status === TradeInOfferStatus.ACCEPTED_DEFERRED 
            || this.offer.status === TradeInOfferStatus.RMA_EXPIRED)
        && !(this.device.status === TradeInDeviceStatus.IN_TRANSIT
            || this.device.status === TradeInDeviceStatus.GOOD_CONDITION
            || this.device.status === TradeInDeviceStatus.FMIP_ON
            || this.device.status === TradeInDeviceStatus.REJECTED);
    }

    public get isOrderShipped(): boolean {
        return this.offer.status === TradeInOfferStatus.RMA_SET && !this.device.status;
    }

    public get isGracePeriod(): boolean {
        return this.offer.status === TradeInOfferStatus.RMA_EXPIRED;
    }

    public get isGracePeriodExpired(): boolean {
        return this.offer.status === TradeInOfferStatus.GRACE_PERIOD_EXPIRED;
    }

    public get isDeviceInTransit(): boolean {
        return this.device.status === TradeInDeviceStatus.IN_TRANSIT;
    }

    public get isAccepted(): boolean {
        return (this.device.status === TradeInDeviceStatus.GOOD_CONDITION || this.offer.status === TradeInOfferStatus.ACCEPTED);
    }

    public get isRejected(): boolean {
        return this.device.status === TradeInDeviceStatus.REJECTED;
    }

    public get isFmipOn(): boolean {
        return this.device.status === TradeInDeviceStatus.FMIP_ON;
    }

    public get isCancelled(): boolean {
        return this.offer.status === TradeInOfferStatus.CANCELLED;
    }

    public get isAppleDevice(): boolean {
        return (this.device.make && this.device.make.toUpperCase() === Manufacturer.APPLE);
    }

    public get isTrackingNoAvailable(): boolean {
        return (!!this.trackingNumber);
    }

    public get isWaitingDecision(): boolean {
        return this.offer?.adjustedOffer?.status === TradeInDeviceStatus.WAITDECISION;
    }

    public get isProcessingStatus(): boolean {
        return this.offer?.adjustedOffer?.status === TradeInDeviceStatus.PROCESSING;
    }

    public get isCompletedStatus(): boolean {
        return this.offer?.adjustedOffer?.status === TradeInDeviceStatus.COMPLETED;
    }

    public get isReceivedStatus(): boolean {
        return this.offer?.adjustedOffer?.status === TradeInDeviceStatus.RECEIVED;
    }

    public get isNotReceivedStatus(): boolean {
        return this.offer?.adjustedOffer?.status === TradeInDeviceStatus.NOTRECEIVED;
    }

    public get isAdjustedOfferAcceptedStatus(): boolean {
        return this.offer?.adjustedOffer?.customerDecision === TradeInDeviceStatus.ACCEPTED;
    }

    public get isAdjustedOfferRejectedStatus(): boolean {
        return this.offer?.adjustedOffer?.customerDecision === TradeInDeviceStatus.REJECTED;
    }

    public get isOfferRejected(): boolean {
        return this.offer?.status === TradeInDeviceStatus.REJECTED;
    }
}
